const LEGACY_MARKET_LABELS = {
  HOME_WIN: "Home Win",
  AWAY_WIN: "Away Win",
  DRAW: "Draw",
  "1": "Home Win",
  X: "Draw",
  "2": "Away Win",
  "1X": "Home or Draw",
  X2: "Draw or Away",
  "12": "Home or Away",
  DC_1X: "Home or Draw",
  DC_X2: "Draw or Away",
  DC_12: "Home or Away",
  HOME_DNB: "Draw No Bet (Home)",
  AWAY_DNB: "Draw No Bet (Away)",
  DNB_HOME: "Draw No Bet (Home)",
  DNB_AWAY: "Draw No Bet (Away)",
  BTTS_Y: "Both Teams To Score: Yes",
  BTTS_N: "Both Teams To Score: No",
  BTTS_YES: "Both Teams To Score: Yes",
  BTTS_NO: "Both Teams To Score: No",
  HSH_1H: "Highest Scoring Half: 1st Half",
  HSH_DRAW: "Highest Scoring Half: Draw",
  HSH_2H: "Highest Scoring Half: 2nd Half",
  FIRST_INNING_RUN_Y: "YRFI — Run in the 1st Inning",
  FIRST_INNING_RUN_N: "NRFI — No Run in the 1st Inning",
};

const LEGACY_MARKET_SHORT_LABELS = {
  HOME_WIN: "Home",
  AWAY_WIN: "Away",
  DRAW: "Draw",
  "1X": "1X",
  X2: "X2",
  "12": "12",
  DC_1X: "1X",
  DC_X2: "X2",
  DC_12: "12",
  HOME_DNB: "DNB H",
  AWAY_DNB: "DNB A",
  DNB_HOME: "DNB H",
  DNB_AWAY: "DNB A",
  BTTS_Y: "BTTS Y",
  BTTS_N: "BTTS N",
  HSH_1H: "HSH 1H",
  HSH_DRAW: "HSH D",
  HSH_2H: "HSH 2H",
  FIRST_INNING_RUN_Y: "YRFI",
  FIRST_INNING_RUN_N: "NRFI",
};

function normalizeLineToken(token) {
  const raw = String(token ?? "").trim().toUpperCase();
  if (!raw) return "";
  if (/^N\d+(\.\d+)?$/.test(raw)) return `-${raw.slice(1)}`;
  if (/^P\d+(\.\d+)?$/.test(raw)) return `+${raw.slice(1)}`;
  if (/^[+-]?\d+(\.\d+)?$/.test(raw)) return raw;
  return String(token).trim();
}

function fallbackLabel(key) {
  const raw = String(key ?? "").trim();
  if (!raw) return "Unknown Market";
  return raw;
}

export function formatMarketName(marketKey, options = {}) {
  const short = Boolean(options.short);
  const raw = String(marketKey ?? "").trim();
  if (!raw) return "Unknown Market";

  const m = raw.toUpperCase();
  const labelMap = short ? LEGACY_MARKET_SHORT_LABELS : LEGACY_MARKET_LABELS;

  if (labelMap[m]) return labelMap[m];

  const firstHalfMatch = m.match(/^FH_([OU])(\d+(\.\d+)?)$/);
  if (firstHalfMatch) {
    const side = firstHalfMatch[1] === "O" ? "Over" : "Under";
    const sideShort = firstHalfMatch[1] === "O" ? "O" : "U";
    const line = firstHalfMatch[2];
    return short ? `1H ${sideShort}${line}` : `1st Half ${side} ${line}`;
  }

  const asianHandicapMatch = m.match(/^AH_(HOME|AWAY)_(.+)$/);
  if (asianHandicapMatch) {
    const side = asianHandicapMatch[1];
    const line = normalizeLineToken(asianHandicapMatch[2]);
    if (short) return `AH ${side === "HOME" ? "H" : "A"} ${line}`;
    return `Asian Handicap ${side === "HOME" ? "Home" : "Away"} ${line}`;
  }

  const handicapResultMatch = m.match(/^HR_(HOME|DRAW|AWAY)_(.+)$/);
  if (handicapResultMatch) {
    const side = handicapResultMatch[1];
    const line = normalizeLineToken(handicapResultMatch[2]);
    if (short) {
      const sideToken = side === "HOME" ? "H" : side === "AWAY" ? "A" : "D";
      return `HR ${sideToken} ${line}`;
    }
    const labelSide = side === "HOME" ? "Home" : side === "AWAY" ? "Away" : "Draw";
    return `Handicap Result ${labelSide} ${line}`;
  }

  const teamTotalMatch = m.match(/^(HOME|AWAY)_TOTAL_([OU])(\d+(\.\d+)?)$/);
  if (teamTotalMatch) {
    const team = teamTotalMatch[1] === "HOME" ? "Home" : "Away";
    const direction = teamTotalMatch[2] === "O" ? "Over" : "Under";
    const line = teamTotalMatch[3];
    return short ? `${team} ${teamTotalMatch[2]}${line}` : `${team} Team Total ${direction} ${line}`;
  }

  const gameTotalMatch = m.match(/^GAME_TOTAL_([OU])(\d+(\.\d+)?)$/);
  if (gameTotalMatch) {
    const direction = gameTotalMatch[1] === "O" ? "Over" : "Under";
    const line = gameTotalMatch[2];
    return short ? `${gameTotalMatch[1]}${line}` : `Game Total ${direction} ${line}`;
  }

  const statAwareMatch = m.match(/^(?:(HOME|AWAY)_)?(CORNERS|CARDS|SOG)_([OU])(\d+(\.\d+)?)$/);
  if (statAwareMatch) {
    const side = statAwareMatch[1];
    const stat = statAwareMatch[2];
    const direction = statAwareMatch[3] === "O" ? "Over" : "Under";
    const directionShort = statAwareMatch[3];
    const line = statAwareMatch[4];
    const statLabel =
      stat === "SOG" ? "Shots On Goal" : stat === "CORNERS" ? "Corners" : "Cards";
    if (short) {
      const sideLabel = side ? `${side === "HOME" ? "Home" : "Away"} ` : "";
      return `${sideLabel}${statLabel} ${directionShort}${line}`;
    }
    const sideLabel = side ? `${side === "HOME" ? "Home" : "Away"} ` : "";
    return `${sideLabel}${statLabel} ${direction} ${line}`;
  }

  const overUnderMatch = m.match(/^([OU])(\d+(\.\d+)?)$/);
  if (overUnderMatch) {
    const side = overUnderMatch[1] === "O" ? "Over" : "Under";
    const sideShort = overUnderMatch[1] === "O" ? "O" : "U";
    const line = overUnderMatch[2];
    return short ? `${sideShort}${line}` : `${side} ${line}`;
  }

  return fallbackLabel(raw);
}
